import type {
  CommunityGovernance,
  CommunityGovernanceTeabates,
  CommunityIssueActivityRow,
  CommunityPipeline,
} from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";

const EMPTY_TEABATES: CommunityGovernanceTeabates = {
  total_active_teabates: 0,
  new_in_window: 0,
  alignment_participants: 0,
  total_alignments: 0,
};

/**
 * Read the teabate participation block from a governance response, tolerating
 * the legacy `debates` field that older backend builds may still emit. Returns
 * a zeroed block (never undefined) so downstream UI never crashes on a
 * transitional backend response during a deploy.
 * @param {CommunityGovernance | null | undefined} governance
 * @returns {CommunityGovernanceTeabates}
 */
export function resolveCommunityTeabates(
  governance: CommunityGovernance | null | undefined
): CommunityGovernanceTeabates {
  if (!governance) {
    return EMPTY_TEABATES;
  }
  return governance.teabates ?? governance.debates ?? EMPTY_TEABATES;
}

export const DEFAULT_COMMUNITY_DAYS = 7;
export const COMMUNITY_WINDOW_OPTIONS = [1, 7, 30, 90, 180, 365] as const;
const REGION_CODE_ALIASES: Record<string, string> = {
  UK: "GB",
};

export type IssueCoverageState =
  | "complete"
  | "missing_solution"
  | "missing_action"
  | "missing_both";

export interface PipelineStageViewModel {
  key: "issues" | "analysis" | "strategy" | "execution" | "covered";
  label: string;
  value: number;
  share: number;
  conversionFromPrevious: number | null;
}

/**
 * Format the selected community window label.
 * @param {string} template
 * @param {number} days
 * @returns {string}
 */
export function formatCommunityWindow(template: string, days: number): string {
  return template.replace("{days}", String(days));
}

/**
 * Build a stable issue route even when the issue has no canonical slug.
 * @param {string | null} cname
 * @param {string} name
 * @returns {string}
 */
export function buildIssueHref(cname: string | null, name: string): string {
  if (cname && cname.trim().length > 0) {
    return `/issue/${encodeURIComponent(cname.trim())}`;
  }

  const q = encodeURIComponent(name.trim() || "issue");
  return `/search?type=issues&q=${q}`;
}

/**
 * Build a user profile route from a username.
 * @param {string} username
 * @returns {string}
 */
export function buildUserHref(username: string): string {
  return `/user/${encodeURIComponent(username)}`;
}

/**
 * Format a trend bucket date label for chart axes and tooltips.
 * @param {string} date
 * @param {Intl.DateTimeFormat} dateFormatter
 * @returns {string}
 */
export function formatCommunityDate(date: string, dateFormatter: Intl.DateTimeFormat): string {
  return dateFormatter.format(new Date(`${date}T00:00:00Z`));
}

/**
 * Resolve a country code to a localized country name.
 * @param {string} countryCode
 * @param {Intl.DisplayNames | null} displayNames
 * @param {string} unknownLabel
 * @returns {string}
 */
export function resolveCountryName(
  countryCode: string,
  displayNames: Intl.DisplayNames | null,
  unknownLabel: string
): string {
  const normalized = countryCode.trim();
  if (!normalized || normalized.toUpperCase() === "ZZ") {
    return unknownLabel;
  }

  const normalizedCode = normalized.toUpperCase();
  const aliasedRegionCode = REGION_CODE_ALIASES[normalizedCode] || normalizedCode;
  const isRegionCodeCandidate = /^[A-Z]{2}$/.test(aliasedRegionCode) || /^\d{3}$/.test(aliasedRegionCode);

  if (displayNames && isRegionCodeCandidate) {
    try {
      const resolved = displayNames.of(aliasedRegionCode);
      if (resolved) {
        return resolved;
      }
    } catch {
      // Some profile values are freeform country strings, not ISO region codes.
      // Fall through to a safe human-readable fallback instead of crashing the page.
    }
  }

  if (/^[A-Z\s.'-]{4,}$/.test(normalizedCode)) {
    return normalizedCode
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  return normalized;
}

/**
 * Turn a two-letter country code into a flag emoji.
 * @param {string} countryCode
 * @returns {string}
 */
export function countryCodeToFlagEmoji(countryCode: string): string {
  const normalized = countryCode.trim().toUpperCase();
  const aliasedRegionCode = REGION_CODE_ALIASES[normalized] || normalized;
  if (!/^[A-Z]{2}$/.test(aliasedRegionCode)) {
    return "";
  }

  return aliasedRegionCode
    .split("")
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");
}

/**
 * Build a compact initials token for avatar fallbacks.
 * @param {string} value
 * @returns {string}
 */
export function getInitials(value: string): string {
  const parts = value
    .trim()
    .split(/[\s._-]+/g)
    .filter(Boolean);

  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/**
 * Classify issue coverage based on linked solution/action flags.
 * @param {CommunityIssueActivityRow} row
 * @returns {IssueCoverageState}
 */
export function getIssueCoverageState(row: CommunityIssueActivityRow): IssueCoverageState {
  if (row.has_solution && row.has_action) {
    return "complete";
  }
  if (!row.has_solution && !row.has_action) {
    return "missing_both";
  }
  if (!row.has_solution) {
    return "missing_solution";
  }
  return "missing_action";
}

/**
 * Build the pipeline stage view model used by the funnel section.
 * @param {CommunityPipeline} pipeline
 * @param {CommunityUiStrings} labels
 * @returns {PipelineStageViewModel[]}
 */
export function getPipelineStages(
  pipeline: CommunityPipeline,
  labels: CommunityUiStrings
): PipelineStageViewModel[] {
  const totalIssues = Math.max(0, pipeline.total_issues);
  const baseStages: Array<Pick<PipelineStageViewModel, "key" | "label" | "value">> = [
    {
      key: "issues",
      label: labels.pipelineStageIssues,
      value: pipeline.total_issues,
    },
    {
      key: "analysis",
      label: labels.pipelineStageAnalysis,
      value: pipeline.with_analysis,
    },
    {
      key: "strategy",
      label: labels.pipelineStageStrategy,
      value: pipeline.with_strategy,
    },
    {
      key: "execution",
      label: labels.pipelineStageExecution,
      value: pipeline.with_execution,
    },
    {
      key: "covered",
      label: labels.pipelineStageCovered,
      value: pipeline.fully_covered,
    },
  ];

  return baseStages.map((stage, index) => {
    const previous = index === 0 ? null : baseStages[index - 1];
    return {
      ...stage,
      share: totalIssues > 0 ? stage.value / totalIssues : 0,
      conversionFromPrevious:
        previous && previous.value > 0 ? stage.value / previous.value : null,
    };
  });
}

